import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Droplets, 
  Zap, 
  Shield, 
  Leaf, 
  ChevronRight, 
  Beaker, 
  Settings, 
  Thermometer, 
  CheckCircle2, 
  ArrowRight, 
  TrendingUp 
} from 'lucide-react';
import './App.css';

import { Navbar } from './components/Navbar';
import { InvestorSection } from './components/InvestorSection';
import { ContactSection } from './components/ContactSection';
import { Footer } from './components/Footer';
import { LegalModals } from './components/LegalModals';

gsap.registerPlugin(ScrollTrigger);

// Lazy Image Component with performance attributes
const LazyImage = ({ 
  src, 
  alt, 
  className, 
  loading = 'lazy' 
}: { 
  src: string; 
  alt: string; 
  className?: string; 
  loading?: 'lazy' | 'eager' 
}) => (
  <img 
    src={src} 
    alt={alt} 
    className={className}
    loading={loading}
    decoding="async"
  />
);

function App() {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  
  const mainRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const pathsRef = useRef<HTMLDivElement>(null);
  const consumerRef = useRef<HTMLDivElement>(null);
  const industrialRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const specsRef = useRef<HTMLDivElement>(null);
  const techRef = useRef<HTMLDivElement>(null);
  const useCasesRef = useRef<HTMLDivElement>(null);
  const safetyRef = useRef<HTMLDivElement>(null);
  const compactRef = useRef<HTMLDivElement>(null);
  const investorRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  // GSAP Animations Context
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero entrance animation
      const heroTl = gsap.timeline();
      heroTl.fromTo('.hero-card', 
        { opacity: 0, y: 24 }, 
        { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out' }
      )
      .fromTo('.hero-headline span', 
        { opacity: 0, y: 18 }, 
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.05, ease: 'power2.out' }, 
        '-=0.5'
      )
      .fromTo('.hero-subheadline', 
        { opacity: 0, y: 12 }, 
        { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 
        '-=0.3'
      )
      .fromTo('.right-panel-card', 
        { opacity: 0, x: '8vw' }, 
        { opacity: 1, x: 0, duration: 0.7, stagger: 0.12, ease: 'power2.out' }, 
        '-=0.6'
      );

      // Hero scroll-driven exit
      ScrollTrigger.create({
        trigger: heroRef.current,
        start: 'top top',
        end: '+=130%',
        pin: true,
        scrub: 0.6,
        onUpdate: (self) => {
          const progress = self.progress;
          if (progress > 0.7) {
            const exitProgress = (progress - 0.7) / 0.3;
            gsap.set('.hero-card', { 
              x: -18 * exitProgress + 'vw', 
              opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 
            });
            gsap.set('.right-panel', { 
              x: 10 * exitProgress + 'vw', 
              opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 
            });
          }
        }
      });

      // Two Paths Section
      ScrollTrigger.create({
        trigger: pathsRef.current,
        start: 'top top',
        end: '+=130%',
        pin: true,
        scrub: 0.6,
        onUpdate: (self) => {
          const progress = self.progress;
          const leftCard = document.querySelector('.paths-left-card');
          const rightCard = document.querySelector('.paths-right-card');
          const centerLabel = document.querySelector('.paths-center-label');
          
          if (progress <= 0.3) {
            const enterProgress = progress / 0.3;
            gsap.set(leftCard, { x: -55 * (1 - enterProgress) + 'vw', opacity: enterProgress });
            gsap.set(rightCard, { x: 55 * (1 - enterProgress) + 'vw', opacity: enterProgress });
            gsap.set(centerLabel, { opacity: enterProgress, y: 10 * (1 - enterProgress) });
          } else if (progress <= 0.7) {
            gsap.set(leftCard, { x: 0, opacity: 1 });
            gsap.set(rightCard, { x: 0, opacity: 1 });
            gsap.set(centerLabel, { opacity: 1, y: 0 });
          } else {
            const exitProgress = (progress - 0.7) / 0.3;
            gsap.set(leftCard, { x: -12 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.7 });
            gsap.set(rightCard, { x: 12 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.7 });
            gsap.set(centerLabel, { opacity: progress > 0.95 ? 0 : 1 - exitProgress });
          }
        }
      });

      // Consumer Section
      ScrollTrigger.create({
        trigger: consumerRef.current,
        start: 'top top',
        end: '+=130%',
        pin: true,
        scrub: 0.6,
        onUpdate: (self) => {
          const progress = self.progress;
          const leftImg = document.querySelector('.consumer-left-img');
          const rightText = document.querySelector('.consumer-right-text');
          
          if (progress <= 0.3) {
            const enterProgress = progress / 0.3;
            gsap.set(leftImg, { x: -60 * (1 - enterProgress) + 'vw', opacity: enterProgress });
            gsap.set(rightText, { x: 20 * (1 - enterProgress) + 'vw', opacity: enterProgress });
          } else if (progress <= 0.7) {
            gsap.set(leftImg, { x: 0, opacity: 1 });
            gsap.set(rightText, { x: 0, opacity: 1 });
          } else {
            const exitProgress = (progress - 0.7) / 0.3;
            gsap.set(leftImg, { x: -10 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
            gsap.set(rightText, { x: 8 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
          }
        }
      });

      // Industrial Section
      ScrollTrigger.create({
        trigger: industrialRef.current,
        start: 'top top',
        end: '+=130%',
        pin: true,
        scrub: 0.6,
        onUpdate: (self) => {
          const progress = self.progress;
          const rightImg = document.querySelector('.industrial-right-img');
          const leftText = document.querySelector('.industrial-left-text');
          
          if (progress <= 0.3) {
            const enterProgress = progress / 0.3;
            gsap.set(rightImg, { x: 60 * (1 - enterProgress) + 'vw', opacity: enterProgress });
            gsap.set(leftText, { x: -20 * (1 - enterProgress) + 'vw', opacity: enterProgress });
          } else if (progress <= 0.7) {
            gsap.set(rightImg, { x: 0, opacity: 1 });
            gsap.set(leftText, { x: 0, opacity: 1 });
          } else {
            const exitProgress = (progress - 0.7) / 0.3;
            gsap.set(rightImg, { x: 10 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
            gsap.set(leftText, { x: -8 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
          }
        }
      });

      // Steps Section
      gsap.utils.toArray('.step-card').forEach((card) => {
        gsap.fromTo(card as Element, 
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: card as Element,
              start: 'top 80%',
              end: 'top 55%',
              scrub: 0.4
            }
          }
        );
      });

      // Specs Section
      ScrollTrigger.create({
        trigger: specsRef.current,
        start: 'top top',
        end: '+=130%',
        pin: true,
        scrub: 0.6,
        onUpdate: (self) => {
          const progress = self.progress;
          const leftCard = document.querySelector('.specs-left-card');
          const rightPanel = document.querySelector('.specs-right-panel');
          
          if (progress <= 0.3) {
            const enterProgress = progress / 0.3;
            gsap.set(leftCard, { x: -60 * (1 - enterProgress) + 'vw', opacity: enterProgress });
            gsap.set(rightPanel, { x: 20 * (1 - enterProgress) + 'vw', opacity: enterProgress });
          } else if (progress <= 0.7) {
            gsap.set(leftCard, { x: 0, opacity: 1 });
            gsap.set(rightPanel, { x: 0, opacity: 1 });
          } else {
            const exitProgress = (progress - 0.7) / 0.3;
            gsap.set(leftCard, { x: -10 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
            gsap.set(rightPanel, { x: 8 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
          }
        }
      });

      // Tech Section
      ScrollTrigger.create({
        trigger: techRef.current,
        start: 'top top',
        end: '+=130%',
        pin: true,
        scrub: 0.6,
        onUpdate: (self) => {
          const progress = self.progress;
          const centerCard = document.querySelector('.tech-center-card');
          const bgGlow = document.querySelector('.tech-bg-glow');
          
          if (progress <= 0.3) {
            const enterProgress = progress / 0.3;
            gsap.set(centerCard, { scale: 0.9 + 0.1 * enterProgress, opacity: enterProgress });
            gsap.set(bgGlow, { opacity: enterProgress * 0.8 });
          } else if (progress <= 0.7) {
            gsap.set(centerCard, { scale: 1, opacity: 1 });
            gsap.set(bgGlow, { opacity: 0.8 });
          } else {
            const exitProgress = (progress - 0.7) / 0.3;
            gsap.set(centerCard, { scale: 1 - 0.05 * exitProgress, opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
            gsap.set(bgGlow, { opacity: (1 - exitProgress) * 0.8 });
          }
        }
      });

      // Investor Section
      ScrollTrigger.create({
        trigger: investorRef.current,
        start: 'top top',
        end: '+=130%',
        pin: true,
        scrub: 0.6,
        onUpdate: (self) => {
          const progress = self.progress;
          const leftContent = document.querySelector('.investor-left-content');
          const rightForm = document.querySelector('.investor-right-form');
          
          if (progress <= 0.3) {
            const enterProgress = progress / 0.3;
            gsap.set(leftContent, { x: -60 * (1 - enterProgress) + 'vw', opacity: enterProgress });
            gsap.set(rightForm, { x: 20 * (1 - enterProgress) + 'vw', opacity: enterProgress });
          } else if (progress <= 0.7) {
            gsap.set(leftContent, { x: 0, opacity: 1 });
            gsap.set(rightForm, { x: 0, opacity: 1 });
          } else {
            const exitProgress = (progress - 0.7) / 0.3;
            gsap.set(leftContent, { x: -10 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
            gsap.set(rightForm, { x: 8 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
          }
        }
      });

      // Compact Section
      ScrollTrigger.create({
        trigger: compactRef.current,
        start: 'top top',
        end: '+=130%',
        pin: true,
        scrub: 0.6,
        onUpdate: (self) => {
          const progress = self.progress;
          const leftImg = document.querySelector('.compact-left-img');
          const rightText = document.querySelector('.compact-right-text');
          
          if (progress <= 0.3) {
            const enterProgress = progress / 0.3;
            gsap.set(leftImg, { x: -60 * (1 - enterProgress) + 'vw', opacity: enterProgress });
            gsap.set(rightText, { x: 20 * (1 - enterProgress) + 'vw', opacity: enterProgress });
          } else if (progress <= 0.7) {
            gsap.set(leftImg, { x: 0, opacity: 1 });
            gsap.set(rightText, { x: 0, opacity: 1 });
          } else {
            const exitProgress = (progress - 0.7) / 0.3;
            gsap.set(leftImg, { x: -10 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
            gsap.set(rightText, { x: 8 * exitProgress + 'vw', opacity: progress > 0.95 ? 0 : 1 - exitProgress * 0.75 });
          }
        }
      });

      // FAQ items animation
      gsap.utils.toArray('.faq-item').forEach((item, i) => {
        gsap.fromTo(item as Element, 
          { y: 24, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
            delay: i * 0.06,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: item as Element,
              start: 'top 85%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

    }, mainRef);

    return () => ctx.revert();
  }, []);

  const handleNavigate = (sectionId: string) => {
    const map: Record<string, React.RefObject<HTMLDivElement | null>> = {
      hero: heroRef,
      consumer: consumerRef,
      industrial: industrialRef,
      tech: techRef,
      specs: specsRef,
      investor: investorRef,
      contact: contactRef,
    };
    const targetRef = map[sectionId];
    if (targetRef && targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div ref={mainRef} className="relative bg-[#F6F7F9] text-[#0B0C0F]">
      {/* Subtle Grain Overlay */}
      <div className="grain-overlay" />
      
      {/* Navigation Header */}
      <Navbar onNavigate={handleNavigate} />

      {/* Section 1: Hero */}
      <section ref={heroRef} className="section-pinned bg-[#F6F7F9] light-pool z-10">
        <div className="absolute inset-0 flex items-center justify-center pt-20">
          {/* Hero Card */}
          <div className="hero-card absolute left-[6vw] top-[14vh] w-[62vw] h-[72vh] uv-card overflow-hidden shadow-xl">
            <LazyImage 
              src="/images/hero_water_splash.jpg" 
              alt="Water splash purification" 
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
            <div className="absolute bottom-[10%] left-[5%] w-[75%] text-white">
              <p className="uv-micro-label text-cyan-400 mb-3 tracking-widest font-mono">WATER PURIFICATION TECHNOLOGY</p>
              <h1 className="hero-headline text-4xl md:text-6xl lg:text-7xl font-light mb-4 tracking-tight">
                {'Pure water in seconds.'.split(' ').map((word, i) => (
                  <span key={i} className="inline-block mr-[0.25em]">{word}</span>
                ))}
              </h1>
              <p className="hero-subheadline text-white/85 text-base md:text-lg max-w-lg mb-6 leading-relaxed">
                Pulsed UV‑C without chemicals. Compact modules for homes, laboratories, and mission-critical field operations.
              </p>
              <button 
                onClick={() => handleNavigate('tech')}
                className="inline-flex items-center gap-2 text-white bg-white/10 hover:bg-white/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 transition-all group"
              >
                <span className="text-sm font-medium">Explore the technology</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-cyan-400" />
              </button>
            </div>
          </div>
          
          {/* Right Panel Cards */}
          <div className="right-panel absolute left-[70vw] top-[14vh] w-[24vw] h-[72vh] flex flex-col gap-4">
            <div 
              onClick={() => handleNavigate('consumer')}
              className="right-panel-card uv-card flex-1 p-6 cursor-pointer hover:translate-y-[-4px] transition-all group border border-slate-200/80 bg-white"
            >
              <div className="w-10 h-10 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mb-3">
                <Droplets className="w-5 h-5 text-[#06B6D4]" />
              </div>
              <h3 className="text-lg font-medium mb-1 text-slate-900">Consumer Wand</h3>
              <p className="text-xs text-[#6B7280]">Portable, rechargeable pulsed disinfection for daily use.</p>
              <ArrowRight className="w-4 h-4 text-[#06B6D4] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            <div 
              onClick={() => handleNavigate('industrial')}
              className="right-panel-card uv-card flex-1 p-6 cursor-pointer hover:translate-y-[-4px] transition-all group border border-slate-200/80 bg-white"
            >
              <div className="w-10 h-10 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mb-3">
                <Settings className="w-5 h-5 text-[#06B6D4]" />
              </div>
              <h3 className="text-lg font-medium mb-1 text-slate-900">Industrial Module</h3>
              <p className="text-xs text-[#6B7280]">Rack-mounted inline modules for continuous flow operations.</p>
              <ArrowRight className="w-4 h-4 text-[#06B6D4] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            <div 
              onClick={() => handleNavigate('investor')}
              className="right-panel-card uv-card p-6 cursor-pointer hover:translate-y-[-4px] transition-all group border-2 border-[#06B6D4]/30 bg-cyan-950 text-white"
            >
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center mb-3 text-cyan-400">
                <TrendingUp className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-medium mb-1 text-white">Invest Early</h3>
              <p className="text-xs text-cyan-200/80">Seed round open — view syndicate opportunity.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Two Paths */}
      <section ref={pathsRef} className="section-pinned bg-[#F6F7F9] dot-grid z-20">
        <div className="absolute inset-0 flex items-center justify-center pt-20">
          <p className="paths-center-label uv-micro-label absolute top-[10vh] left-1/2 -translate-x-1/2 text-slate-500">
            CHOOSE YOUR CONFIGURATION
          </p>
          
          {/* Left Card - Consumer */}
          <div 
            onClick={() => handleNavigate('consumer')}
            className="paths-left-card absolute left-[6vw] top-[14vh] w-[42vw] h-[72vh] uv-card overflow-hidden cursor-pointer hover:shadow-xl transition-all border border-slate-200 bg-white"
          >
            <div className="h-[55%] overflow-hidden">
              <LazyImage 
                src="/images/consumer_pour_glass.jpg" 
                alt="Consumer pouring purified water" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-6 h-[45%] flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono text-cyan-600 uppercase font-semibold">PORTABLE DISINFECTION</span>
                <h3 className="text-2xl font-medium mt-1 mb-2 text-slate-900">Consumer</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  A high-fluence handheld wand for daily hydration, travel, and expeditions. Disinfects 0.5–2 L in 10–60 seconds.
                </p>
              </div>
              <button className="inline-flex items-center gap-2 text-[#06B6D4] text-sm font-semibold group">
                <span>Explore consumer features</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
          
          {/* Right Card - Industrial */}
          <div 
            onClick={() => handleNavigate('industrial')}
            className="paths-right-card absolute left-[52vw] top-[14vh] w-[42vw] h-[72vh] uv-card overflow-hidden cursor-pointer hover:shadow-xl transition-all border border-slate-200 bg-white"
          >
            <div className="h-[55%] overflow-hidden">
              <LazyImage 
                src="/images/industrial_valve_closeup.jpg" 
                alt="Industrial water system valve" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-6 h-[45%] flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono text-cyan-600 uppercase font-semibold">CONTINUOUS FLOW</span>
                <h3 className="text-2xl font-medium mt-1 mb-2 text-slate-900">Industrial</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Rack-ready inline modules for biotech laboratories, bottling facilities, and mobile filtration rigs.
                </p>
              </div>
              <button className="inline-flex items-center gap-2 text-[#06B6D4] text-sm font-semibold group">
                <span>Explore industrial specs</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Consumer */}
      <section ref={consumerRef} className="section-pinned bg-[#F6F7F9] z-30">
        <div className="absolute inset-0 flex items-center justify-center pt-20">
          <div className="consumer-left-img absolute left-[6vw] top-[14vh] w-[56vw] h-[72vh] uv-card overflow-hidden shadow-lg">
            <LazyImage 
              src="/images/consumer_wand.jpg" 
              alt="UV Pulser Consumer Wand device" 
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="consumer-right-text absolute left-[64vw] top-[14vh] w-[30vw] h-[72vh] flex flex-col justify-center">
            <p className="uv-micro-label mb-3 text-cyan-600 font-mono">CONSUMER SERIES</p>
            <h2 className="text-4xl md:text-5xl font-light mb-5 tracking-tight text-slate-900">Clean water anywhere.</h2>
            <p className="text-[#6B7280] mb-6 leading-relaxed text-sm">
              The solid-state wand delivers calibrated high-peak UV‑C pulses in seconds. No mercury lamps, no chemical taste, no waiting.
            </p>
            <ul className="space-y-3.5 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#06B6D4] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-700">10–60 sec rapid treatment cycle (0.5–2 L)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#06B6D4] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-700">OLED operational display & single-button trigger</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#06B6D4] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-700">IP67 waterproof rating with USB‑C fast charging</span>
              </li>
            </ul>
            <button 
              onClick={() => handleNavigate('contact')}
              className="uv-btn-primary w-fit text-sm py-2.5 px-6"
            >
              Request Consumer Kit
            </button>
          </div>
        </div>
      </section>

      {/* Section 4: Industrial */}
      <section ref={industrialRef} className="section-pinned bg-[#F6F7F9] z-40">
        <div className="absolute inset-0 flex items-center justify-center pt-20">
          <div className="industrial-left-text absolute left-[6vw] top-[14vh] w-[30vw] h-[72vh] flex flex-col justify-center">
            <p className="uv-micro-label mb-3 text-cyan-600 font-mono">INDUSTRIAL SYSTEMS</p>
            <h2 className="text-4xl md:text-5xl font-light mb-5 tracking-tight text-slate-900">Built for scale.</h2>
            <p className="text-[#6B7280] mb-6 leading-relaxed text-sm">
              A rack-mounted pulsed UV module engineered for continuous inline disinfection, tool-free service, and telemetry integration.
            </p>
            <ul className="space-y-3.5 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#06B6D4] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-700">Flow-rated dosing with real-time UV sensor telemetry</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#06B6D4] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-700">Tool-free cartridge replacement in under 3 minutes</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#06B6D4] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-700">Modular parallel stacking for high-capacity facilities</span>
              </li>
            </ul>
            <button 
              onClick={() => handleNavigate('contact')}
              className="uv-btn-primary w-fit text-sm py-2.5 px-6"
            >
              Request Industrial Specs
            </button>
          </div>
          
          <div className="industrial-right-img absolute left-[38vw] top-[14vh] w-[56vw] h-[72vh] uv-card overflow-hidden shadow-lg">
            <LazyImage 
              src="/images/industrial_module.jpg" 
              alt="UV Pulser Industrial Module" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Section 5: How It Works */}
      <section ref={stepsRef} className="section-flowing bg-[#F6F7F9] py-20 z-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="uv-micro-label mb-3 text-cyan-600 font-mono">OPERATING WORKFLOW</p>
            <h2 className="text-4xl md:text-5xl font-light text-slate-900">Three steps to pure water.</h2>
          </div>
          
          <div className="space-y-6">
            <div className="step-card uv-card w-full h-[34vh] flex overflow-hidden border border-slate-200 bg-white">
              <div className="w-[55%] overflow-hidden">
                <LazyImage 
                  src="/images/step_place_water.jpg" 
                  alt="Place UV Pulser in water" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-[45%] p-8 flex flex-col justify-center">
                <span className="uv-micro-label text-2xl font-mono text-cyan-600 mb-2">01</span>
                <h3 className="text-2xl font-medium mb-2 text-slate-900">Place</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Submerge the wand or engage the inline bypass. Optical immersion sensors confirm water presence instantly.
                </p>
              </div>
            </div>
            
            <div className="step-card uv-card w-full h-[34vh] flex overflow-hidden border border-slate-200 bg-white">
              <div className="w-[55%] overflow-hidden">
                <LazyImage 
                  src="/images/step_uv_glow.jpg" 
                  alt="UV-C pulse technology in action" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-[45%] p-8 flex flex-col justify-center">
                <span className="uv-micro-label text-2xl font-mono text-cyan-600 mb-2">02</span>
                <h3 className="text-2xl font-medium mb-2 text-slate-900">Pulsed UV‑C</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Microsecond xenon flash pulses inactivate microbial pathogens, viruses, and cysts at the DNA/RNA level.
                </p>
              </div>
            </div>
            
            <div className="step-card uv-card w-full h-[34vh] flex overflow-hidden border border-slate-200 bg-white">
              <div className="w-[55%] overflow-hidden">
                <LazyImage 
                  src="/images/step_drink_glass.jpg" 
                  alt="Drink pure purified water" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-[45%] p-8 flex flex-col justify-center">
                <span className="uv-micro-label text-2xl font-mono text-cyan-600 mb-2">03</span>
                <h3 className="text-2xl font-medium mb-2 text-slate-900">Ready</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Purified water is immediately potable—with zero chemical additions, zero ozone residue, and zero temperature rise.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Performance Specs */}
      <section ref={specsRef} className="section-pinned bg-[#F6F7F9] z-[60]">
        <div className="absolute inset-0 flex items-center justify-center pt-20">
          <div className="specs-left-card absolute left-[6vw] top-[14vh] w-[56vw] h-[72vh] uv-card overflow-hidden shadow-xl">
            <LazyImage 
              src="/images/tech_lamp_assembly.jpg" 
              alt="UV Lamp Assembly Technology" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
            <div className="absolute bottom-[8%] left-[5%] text-white">
              <p className="uv-micro-label text-cyan-400 mb-3 font-mono">ENGINEERING METRICS</p>
              <h2 className="text-4xl md:text-5xl font-light mb-4 tracking-tight">High dose. Compact size.</h2>
              <p className="text-white/85 mb-6 max-w-md text-sm leading-relaxed">
                Engineered for certified pathogen inactivation without bulky reactors or toxic mercury lamps.
              </p>
              <div className="flex gap-8">
                <div>
                  <p className="text-3xl font-light text-[#06B6D4]">80</p>
                  <p className="text-xs text-white/70 uppercase tracking-wider font-mono">mJ/cm² dose</p>
                </div>
                <div>
                  <p className="text-3xl font-light text-[#06B6D4]">12</p>
                  <p className="text-xs text-white/70 uppercase tracking-wider font-mono">L/min flow</p>
                </div>
                <div>
                  <p className="text-3xl font-light text-[#06B6D4]">10⁹+</p>
                  <p className="text-xs text-white/70 uppercase tracking-wider font-mono">lamp pulses</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="specs-right-panel absolute left-[64vw] top-[14vh] w-[30vw] h-[72vh] flex flex-col gap-4">
            <div className="uv-card flex-1 p-6 flex flex-col justify-center bg-white border border-slate-200/80 shadow-sm">
              <div className="w-12 h-12 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#06B6D4]" />
              </div>
              <h3 className="text-2xl font-medium mb-2 text-slate-900">&gt;4‑log reduction</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">
                Independently verified 99.99% log reduction across broad viral, bacterial, and protozoan spectra.
              </p>
            </div>
            <div className="uv-card flex-1 p-6 flex flex-col justify-center bg-white border border-slate-200/80 shadow-sm">
              <div className="w-12 h-12 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mb-4">
                <Leaf className="w-6 h-6 text-[#06B6D4]" />
              </div>
              <h3 className="text-2xl font-medium mb-2 text-slate-900">100% Mercury-Free</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">
                Eliminates toxic hazardous waste concerns. RoHS-compliant solid-state pulsed discharge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7: Technology */}
      <section ref={techRef} className="section-pinned bg-[#0B0C0F] z-[70]">
        <div className="tech-bg-glow absolute inset-0 opacity-0 pointer-events-none">
          <div className="absolute top-1/2 left-[35%] -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vh] bg-[#06B6D4]/20 rounded-full blur-[120px]" />
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center pt-20">
          <div className="tech-center-card absolute left-[6vw] top-[14vh] w-[62vw] h-[72vh] uv-card overflow-hidden shadow-2xl">
            <LazyImage 
              src="/images/step_uv_glow.jpg" 
              alt="UV Technology Core" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
            <div className="absolute bottom-[8%] left-[5%] text-white">
              <p className="uv-micro-label text-cyan-400 mb-3 font-mono">PROP-TECH INNOVATION</p>
              <h2 className="text-4xl md:text-5xl font-light mb-4 tracking-tight">Pulsed UV‑C, reimagined.</h2>
              <p className="text-white/85 max-w-lg text-sm leading-relaxed">
                Solid-state power electronics coupled with custom low-inductance pulsed discharge geometry.
              </p>
            </div>
          </div>
          
          <div className="absolute left-[70vw] top-[14vh] w-[24vw] h-[72vh] flex flex-col gap-4">
            <div className="tech-right-card uv-card bg-white flex-1 p-5 flex flex-col justify-center border border-slate-800">
              <div className="w-10 h-10 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mb-2.5">
                <Zap className="w-5 h-5 text-[#06B6D4]" />
              </div>
              <h3 className="text-base font-medium mb-1 text-slate-900">GaN / SiC Driver</h3>
              <p className="text-xs text-[#6B7280]">Ultra-fast switching with nanosecond rise times and low thermal dissipation.</p>
            </div>
            <div className="tech-right-card uv-card bg-white flex-1 p-5 flex flex-col justify-center border border-slate-800">
              <div className="w-10 h-10 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mb-2.5">
                <Beaker className="w-5 h-5 text-[#06B6D4]" />
              </div>
              <h3 className="text-base font-medium mb-1 text-slate-900">LTCC Substrate</h3>
              <p className="text-xs text-[#6B7280]">Low Temperature Co-fired Ceramic packaging for extreme dielectric reliability.</p>
            </div>
            <div className="tech-right-card uv-card bg-white flex-1 p-5 flex flex-col justify-center border border-slate-800">
              <div className="w-10 h-10 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mb-2.5">
                <Thermometer className="w-5 h-5 text-[#06B6D4]" />
              </div>
              <h3 className="text-base font-medium mb-1 text-slate-900">Direct Fluid Cooling</h3>
              <p className="text-xs text-[#6B7280]">Targeted hydrodynamic dissipation keeping electronics at optimal longevity.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 8: Use Cases */}
      <section ref={useCasesRef} className="section-flowing bg-[#F6F7F9] py-20 z-[80]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12">
            <p className="uv-micro-label mb-3 text-cyan-600 font-mono">APPLICATIONS</p>
            <h2 className="text-4xl md:text-5xl font-light text-slate-900">Where UV Pulser works.</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="usecase-card uv-card h-[34vh] overflow-hidden group cursor-pointer hover:translate-y-[-4px] transition-all border border-slate-200 bg-white">
              <div className="h-[65%] overflow-hidden">
                <LazyImage 
                  src="/images/usecase_home.jpg" 
                  alt="Home water purification" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 h-[35%] flex items-center justify-between">
                <h3 className="text-lg font-medium text-slate-900">Residential & Under-Sink Systems</h3>
                <ChevronRight className="w-4 h-4 text-cyan-600" />
              </div>
            </div>
            
            <div className="usecase-card uv-card h-[34vh] overflow-hidden group cursor-pointer hover:translate-y-[-4px] transition-all border border-slate-200 bg-white">
              <div className="h-[65%] overflow-hidden">
                <LazyImage 
                  src="/images/usecase_lab.jpg" 
                  alt="Laboratory water purification" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 h-[35%] flex items-center justify-between">
                <h3 className="text-lg font-medium text-slate-900">Laboratories & Clinical Research</h3>
                <ChevronRight className="w-4 h-4 text-cyan-600" />
              </div>
            </div>
            
            <div className="usecase-card uv-card h-[34vh] overflow-hidden group cursor-pointer hover:translate-y-[-4px] transition-all border border-slate-200 bg-white">
              <div className="h-[65%] overflow-hidden">
                <LazyImage 
                  src="/images/usecase_field.jpg" 
                  alt="Field and emergency water purification" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 h-[35%] flex items-center justify-between">
                <h3 className="text-lg font-medium text-slate-900">Emergency Response & Defense Deployment</h3>
                <ChevronRight className="w-4 h-4 text-cyan-600" />
              </div>
            </div>
            
            <div className="usecase-card uv-card h-[34vh] overflow-hidden group cursor-pointer hover:translate-y-[-4px] transition-all border border-slate-200 bg-white">
              <div className="h-[65%] overflow-hidden">
                <LazyImage 
                  src="/images/usecase_plant.jpg" 
                  alt="Industrial plant water purification" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 h-[35%] flex items-center justify-between">
                <h3 className="text-lg font-medium text-slate-900">Bottling Plants & Commercial Infrastructure</h3>
                <ChevronRight className="w-4 h-4 text-cyan-600" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 9: Safety & Sustainability */}
      <section ref={safetyRef} className="section-flowing bg-[#F6F7F9] py-20 z-[90]">
        <div className="max-w-6xl mx-auto px-6 space-y-6">
          <div className="safety-card uv-card h-[38vh] flex overflow-hidden border border-slate-200 bg-white">
            <div className="w-[55%] overflow-hidden">
              <LazyImage 
                src="/images/safety_hand_device.jpg" 
                alt="Safety-first device design" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="w-[45%] p-8 flex flex-col justify-center">
              <div className="w-12 h-12 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#06B6D4]" />
              </div>
              <h3 className="text-2xl font-medium mb-3 text-slate-900">Safety-first design.</h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                Optical immersion lock-out prevents accidental unshielded activation. Shatter-resistant quartz enclosure ensures lifetime protection.
              </p>
            </div>
          </div>
          
          <div className="safety-card uv-card h-[38vh] flex overflow-hidden border border-slate-200 bg-white">
            <div className="w-[55%] overflow-hidden">
              <LazyImage 
                src="/images/consumer_pour_glass.jpg" 
                alt="Sustainable water purification" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="w-[45%] p-8 flex flex-col justify-center">
              <div className="w-12 h-12 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mb-4">
                <Leaf className="w-6 h-6 text-[#06B6D4]" />
              </div>
              <h3 className="text-2xl font-medium mb-3 text-slate-900">Sustainable by default.</h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                Zero chemical supply chains. Xenon flash tube with over 1 billion pulses. 100% recyclable chassis and modular serviceable components.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 10: Investor Section (Modular Component) */}
      <InvestorSection investorRef={investorRef} />

      {/* Section 11: Compact Design */}
      <section ref={compactRef} className="section-pinned bg-[#F6F7F9] dot-grid z-[100]">
        <div className="absolute inset-0 flex items-center justify-center pt-20">
          <div className="compact-left-img absolute left-[6vw] top-[14vh] w-[56vw] h-[72vh] uv-card overflow-hidden shadow-lg">
            <LazyImage 
              src="/images/compact_under_sink.jpg" 
              alt="Compact under-sink installation" 
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="compact-right-text absolute left-[64vw] top-[14vh] w-[30vw] h-[72vh] flex flex-col justify-center">
            <p className="uv-micro-label mb-3 text-cyan-600 font-mono">FORM FACTOR</p>
            <h2 className="text-4xl md:text-5xl font-light mb-5 tracking-tight text-slate-900">Compact by design.</h2>
            <p className="text-[#6B7280] mb-8 leading-relaxed text-sm">
              Fits seamlessly into constrained spaces—under sinks, in standard 19-inch racks, or inside portable field kits.
            </p>
            <button 
              onClick={() => handleNavigate('contact')}
              className="uv-btn-primary w-fit text-sm py-2.5 px-6"
            >
              See Installation Options
            </button>
          </div>
        </div>
      </section>

      {/* Section 12: FAQ */}
      <section ref={faqRef} className="section-flowing bg-[#F6F7F9] py-20 z-[110]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="uv-micro-label mb-3 text-cyan-600 font-mono">FAQ</p>
            <h2 className="text-4xl md:text-5xl font-light text-slate-900">Frequently asked questions.</h2>
          </div>
          
          <div className="space-y-4">
            {[
              { q: "How fast does UV Pulser treat water?", a: "The consumer wand treats 0.5–2 liters in 10–60 seconds depending on fluid turbidity. Industrial continuous-flow modules process up to 12 liters per minute per cell." },
              { q: "Does pulsed UV-C remove particles or heavy chemicals?", a: "Pulsed UV-C rapidly destroys bacterial DNA and viral RNA structures. For heavy particulates or chemical contaminants, we recommend pairing with a sediment pre-filter." },
              { q: "What is the expected lifespan of the pulsed lamp?", a: "The solid-state xenon flash tube is rated for over 10⁹ pulses (approx. 5–7 years of intensive daily treatment). Replacement cartridges take under 3 minutes with tool-free locking." },
              { q: "Is UV Pulser safe for daily family use?", a: "Yes. Integrated optical sensors guarantee the lamp only activates when completely submerged, shielding eyes and skin from direct UV-C exposure." },
              { q: "What certifications does UV Pulser comply with?", a: "Our systems are designed to meet EPA Guide Standards, NSF/ANSI 55 Class A requirements, and CE/FCC/RoHS compliance." },
              { q: "Can it disinfect challenging well or rainwater?", a: "Yes. The high peak irradiance of pulsed UV-C penetrates lower UV-transmittance (UVT) water more effectively than low-pressure mercury lamps." }
            ].map((faq, i) => (
              <div key={i} className="faq-item uv-card p-6 bg-white border border-slate-200/80 shadow-sm">
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <span className="text-base md:text-lg font-medium text-slate-900">{faq.q}</span>
                    <span className="transition group-open:rotate-180">
                      <ChevronRight className="w-5 h-5 text-[#06B6D4] rotate-90" />
                    </span>
                  </summary>
                  <p className="text-[#6B7280] text-sm mt-4 leading-relaxed">
                    {faq.a}
                  </p>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 13: Contact Section (Modular Component) */}
      <ContactSection contactRef={contactRef} />

      {/* Footer (Modular Component) */}
      <Footer 
        onNavigate={handleNavigate}
        onOpenPrivacy={() => setShowPrivacyModal(true)}
        onOpenDisclaimer={() => setShowDisclaimerModal(true)}
      />

      {/* Legal Modals (Modular Component) */}
      <LegalModals 
        showPrivacyModal={showPrivacyModal}
        setShowPrivacyModal={setShowPrivacyModal}
        showDisclaimerModal={showDisclaimerModal}
        setShowDisclaimerModal={setShowDisclaimerModal}
      />
    </div>
  );
}

export default App;
